import { useState } from "react";
import { Store, Truck, BadgeCheck } from "lucide-react";
import { Container, Button, Input } from "@/components/atoms";
import { FormField } from "@/components/molecules";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "@/services/firebase";
import { Seo } from "@/components/seo";

/**
 * DRB-69 — the wholesale channel had a complete back end and no front door.
 *
 * onWholesaleApplication has been deployed since the beginning, firestore.rules carries a
 * hardened create rule for wholesaleApplications (bounded field-by-field under DRB-41, closed to
 * unlisted keys under DRB-68), and the admin Inbox has a Wholesale tab. Nothing anywhere on the
 * public site could write to that collection, so the queue could only ever be empty and the
 * function could never fire. A shop wanting to stock her product had no way to ask.
 *
 * The fields here are EXACTLY the six the rule allows — businessName, contactName, email, phone,
 * message, createdAt. A seventh would be refused by the rule at write time, silently, and present
 * as "the form is broken" with nothing in any log on our side.
 */
export default function Wholesale() {
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    businessName: "",
    contactName: "",
    email: "",
    phone: "",
    message: "",
  });

  function set<K extends keyof typeof form>(k: K, v: string) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  const valid =
    form.businessName.trim().length > 0 &&
    form.contactName.trim().length > 0 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim());

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!valid) return;
    setSending(true);
    setError(null);
    try {
      // The write is what triggers onWholesaleApplication, which alerts orders@ and sends the
      // applicant an acknowledgement.
      await addDoc(collection(db, "wholesaleApplications"), {
        businessName: form.businessName.trim(),
        contactName: form.contactName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        message: form.message.trim(),
        createdAt: serverTimestamp(),
      });
      setSent(true);
    } catch (err) {
      // Say the true thing. A form that claims success and stores nothing is the defect DRB-34
      // found on the contact and newsletter forms; it is not being reintroduced here.
      console.error("[wholesale] application failed", err);
      setError(
        "Something went wrong sending that. Please email us directly at orders@drburnsreleaf.com and we will get right back to you.",
      );
    } finally {
      setSending(false);
    }
  }

  const points = [
    {
      icon: BadgeCheck,
      title: "Physician-formulated",
      body: "Every formula is developed by Dr. Elaine Burns, a naturopathic physician, and tested to a 99.5% accuracy rating.",
    },
    {
      icon: Store,
      title: "Built for clinics and shops",
      body: "Condition-specific products your customers can be guided to, rather than a wall of milligram counts.",
    },
    {
      icon: Truck,
      title: "Straightforward terms",
      body: "Tell us what you carry and roughly what volume you expect, and we will come back with pricing.",
    },
  ];

  return (
    <>
      <Seo
        title="Wholesale"
        path="/wholesale"
        description="Stock physician-formulated CBD from Dr. Elaine Burns. Apply for wholesale pricing for your clinic, pharmacy or retail shop."
      />

      <Container className="grid gap-10 py-12 md:grid-cols-2">
        <div>
          <h1 className="font-condensed text-4xl font-bold text-night-blue">
            Carry DrBurns&rsquo; ReLeaf
          </h1>
          <p className="mt-2 text-night-blue/70">
            For clinics, pharmacies, wellness studios and retailers. Tell us
            about your business and we will get straight back to you with
            wholesale pricing.
          </p>
          <div className="mt-6 flex flex-col gap-4">
            {points.map((pt) => (
              <div
                key={pt.title}
                className="flex items-start gap-3 rounded-card border border-night-blue/10 p-4"
              >
                <pt.icon
                  size={22}
                  className="mt-0.5 shrink-0 text-leaf-green-ink"
                />
                <div>
                  <p className="font-bold text-night-blue">{pt.title}</p>
                  <p className="mt-1 text-sm text-night-blue/70">{pt.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-card border border-night-blue/10 p-6">
          {sent ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
              <h2 className="font-condensed text-2xl font-bold text-leaf-green-ink">
                Application received
              </h2>
              <p className="text-night-blue/70">
                Thank you — we have your details and will be in touch shortly. A
                confirmation is on its way to {form.email.trim()}.
              </p>
            </div>
          ) : (
            <form className="flex flex-col gap-4" onSubmit={submit}>
              <h2 className="font-condensed text-2xl font-bold text-night-blue">
                Wholesale application
              </h2>
              <FormField label="Business name" htmlFor="w-business" required>
                <Input
                  id="w-business"
                  value={form.businessName}
                  onChange={(e) => set("businessName", e.target.value)}
                  maxLength={120}
                  required
                />
              </FormField>
              <FormField label="Your name" htmlFor="w-contact" required>
                <Input
                  id="w-contact"
                  value={form.contactName}
                  onChange={(e) => set("contactName", e.target.value)}
                  maxLength={100}
                  required
                />
              </FormField>
              <FormField label="Email" htmlFor="w-email" required>
                <Input
                  id="w-email"
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder="you@yourbusiness.com"
                  maxLength={254}
                  required
                />
              </FormField>
              <FormField label="Phone" htmlFor="w-phone">
                <Input
                  id="w-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => set("phone", e.target.value)}
                  maxLength={32}
                />
              </FormField>
              <FormField
                label="Tell us about your business"
                htmlFor="w-message"
              >
                <textarea
                  id="w-message"
                  rows={5}
                  maxLength={5000}
                  className="w-full rounded-soft border border-night-blue/15 bg-white px-3 py-2 text-sm text-night-blue focus:border-leaf-green focus:outline-none focus:ring-1 focus:ring-leaf-green"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  placeholder="What do you carry today, and roughly what volume are you thinking?"
                />
              </FormField>
              {error && (
                <p className="rounded-card border-2 border-happy-red/30 bg-happy-red/5 p-3 text-sm text-night-blue">
                  {error}
                </p>
              )}
              <Button type="submit" disabled={!valid || sending}>
                {sending ? "Sending…" : "Submit application"}
              </Button>
            </form>
          )}
        </div>
      </Container>
    </>
  );
}
